import React from "react";
import styled, { keyframes } from "styled-components";
import { device } from "../../../atoms/Utlis/media";
import { MARKETPLACE_GRADIENT } from "./marketplaceColors";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulseRing = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.35; }
  50% { transform: scale(1.08); opacity: 0.55; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
`;

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

export type EmptyStateActionVariant = "primary" | "secondary";

export interface EmptyStateAction {
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
  variant?: EmptyStateActionVariant;
}

export interface MarketplaceEmptyStateProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  actions?: EmptyStateAction[];
  hint?: {
    icon?: React.ReactNode;
    text: string;
  };
  padding?: string;
}

export interface MarketplaceLoadingSpinnerProps {
  padding?: string;
  size?: number;
}

export const MarketplaceEmptyState: React.FC<MarketplaceEmptyStateProps> = ({
  icon,
  title,
  description,
  actions = [],
  hint,
  padding = "24px 0 40px",
}) => (
  <Wrapper $padding={padding}>
    <Card>
      <Glow />
      <IconStage>
        <IconRing />
        <IconCircle>{icon}</IconCircle>
        <SparkleDot $x={-52} $y={-28} $delay={0} />
        <SparkleDot $x={58} $y={-18} $delay={0.4} />
        <SparkleDot $x={-38} $y={42} $delay={0.8} />
      </IconStage>
      <Title>{title}</Title>
      <Description>{description}</Description>
      {actions.length > 0 && (
        <Actions>
          {actions.map((action) =>
            action.variant === "secondary" ? (
              <SecondaryBtn key={action.label} type="button" onClick={action.onClick}>
                {action.icon}
                {action.label}
              </SecondaryBtn>
            ) : (
              <PrimaryBtn key={action.label} type="button" onClick={action.onClick}>
                {action.icon}
                {action.label}
              </PrimaryBtn>
            )
          )}
        </Actions>
      )}
      {hint && (
        <Hint>
          {hint.icon}
          {hint.text}
        </Hint>
      )}
    </Card>
  </Wrapper>
);

export const MarketplaceLoadingSpinner: React.FC<MarketplaceLoadingSpinnerProps> = ({
  padding = "60px 22px",
  size = 36,
}) => (
  <LoadingWrap $padding={padding}>
    <Spinner $size={size} />
  </LoadingWrap>
);

const Wrapper = styled.div<{ $padding: string }>`
  display: flex;
  justify-content: center;
  padding: ${p => p.$padding};
  animation: ${fadeUp} 0.45s ease;
`;

const Card = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: min(100%, 480px);
  padding: 48px 32px 40px;
  border-radius: 20px;
  overflow: hidden;
  @media ${device.tablet} { padding: 36px 20px 32px; }
`;

const Glow = styled.div`
  position: absolute;
  top: -60px;
  left: 50%;
  transform: translateX(-50%);
  width: 280px;
  height: 160px;
  background: radial-gradient(ellipse, rgba(201, 77, 170, 0.15) 0%, rgba(255, 201, 81, 0.08) 50%, transparent 70%);
  pointer-events: none;
`;

const IconStage = styled.div`
  position: relative;
  width: 100px;
  height: 100px;
  margin-bottom: 24px;
`;

const IconRing = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: ${MARKETPLACE_GRADIENT};
  opacity: 0.18;
  animation: ${pulseRing} 3s ease-in-out infinite;
`;

const IconCircle = styled.div`
  position: absolute;
  inset: 10px;
  border-radius: 50%;
  background: linear-gradient(145deg, #fff9f0 0%, #fdf0fa 100%);
  border: 2px solid rgba(201, 77, 170, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c94daa;
  box-shadow: 0 8px 24px rgba(201, 77, 170, 0.15);
  animation: ${float} 4s ease-in-out infinite;
`;

const SparkleDot = styled.div<{ $x: number; $y: number; $delay: number }>`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 8px;
  height: 8px;
  margin-top: ${p => p.$y}px;
  margin-left: ${p => p.$x}px;
  border-radius: 50%;
  background: ${MARKETPLACE_GRADIENT};
  opacity: 0.6;
  animation: ${float} 3s ease-in-out ${p => p.$delay}s infinite;
`;

const Title = styled.h3`
  margin: 0 0 10px;
  font-size: 22px;
  font-weight: 800;
  font-family: Urbanist, sans-serif;
  background: ${MARKETPLACE_GRADIENT};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Description = styled.p`
  margin: 0 0 28px;
  max-width: 340px;
  font-family: Montserrat, sans-serif;
  font-size: 14px;
  line-height: 1.65;
  color: rgba(23, 23, 23, 0.55);
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
`;

const PrimaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 42px;
  padding: 0 22px;
  border: none;
  border-radius: 11px;
  background: ${MARKETPLACE_GRADIENT};
  background-size: 200% auto;
  color: #fff;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 8px 22px rgba(201, 77, 170, 0.28);
  transition: transform 0.2s, box-shadow 0.2s;
  animation: ${shimmer} 4s linear infinite;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 28px rgba(201, 77, 170, 0.35);
  }
`;

const SecondaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 42px;
  padding: 0 20px;
  border: 1px solid rgba(23, 23, 23, 0.14);
  border-radius: 11px;
  background: white;
  color: rgba(23, 23, 23, 0.65);
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s;
  &:hover {
    background: #fafbff;
    border-color: rgba(201, 77, 170, 0.3);
    color: #c94daa;
  }
`;

const Hint = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 24px;
  padding: 8px 16px;
  border-radius: 100px;
  background: rgba(255, 201, 81, 0.12);
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.45);
  svg { color: #f09a3e; flex-shrink: 0; }
`;

const LoadingWrap = styled.div<{ $padding: string }>`
  display: flex;
  justify-content: center;
  padding: ${p => p.$padding};
`;

const Spinner = styled.div<{ $size: number }>`
  width: ${p => p.$size}px;
  height: ${p => p.$size}px;
  border: 3px solid rgba(23, 23, 23, 0.08);
  border-top-color: #c94daa;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
`;

export default MarketplaceEmptyState;
